import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import type { Session } from './session';
import type { GameId } from '../games/ids';
import { connLog } from './connLog';
import { startHostMail } from '../mailbox/hosting';

/**
 * What every live session looks like to the registry, whatever game runs on it: a backgammon
 * `Session` (GameClient) or a generic table session (TableClient) both qualify.
 */
export interface BaseSession {
  matchId: string;
  code: string;
  role: 'host' | 'guest';
  client: {
    getState(): { status: 'connecting' | 'joined' | 'rejected' | 'disconnected' };
    subscribe(listener: () => void): () => void;
  };
  /** True once a hosted session gave up its room code to another device; rejoin, don't wait. */
  readonly lostAddress?: boolean;
  dispose(): void;
}

export interface SessionRegistry {
  /** Increments on every change so consumers re-render. */
  readonly version: number;
  get(slug: string, game: GameId, matchId: string): BaseSession | undefined;
  /**
   * Register a session. A live session (`joined` or `connecting`) under the same key is never
   * replaced: the newcomer is disposed instead and `'kept'` is returned. A late-completing
   * resume attempt must not tear down the table the player is already playing on.
   */
  add(slug: string, game: GameId, session: BaseSession): 'added' | 'kept';
  /** `reason` goes to the connection log, so a closed game can always be explained. */
  remove(slug: string, game: GameId, matchId: string, dispose?: boolean, reason?: string): void;
  all(): BaseSession[];
  /** The live session (any player/game) currently hosting or joined under a room code. */
  liveByCode(code: string): BaseSession | undefined;
}

function isLive(s: BaseSession): boolean {
  const st = s.client.getState().status;
  return st === 'joined' || st === 'connecting';
}

const Ctx = createContext<SessionRegistry | null>(null);

function keyOf(slug: string, game: GameId, matchId: string): string {
  return `${slug}:${game}:${matchId}`;
}

/**
 * Keeps live sessions (client + optional server) alive across route changes. Sessions are keyed
 * by player, game *and* match, so switching player in the same tab never reuses another player's
 * connection.
 */
export function SessionRegistryProvider({
  children,
  initialSessions,
}: {
  children: ReactNode;
  /** Pre-seeded sessions (tests): `[slug, game, session]` triples. */
  initialSessions?: [string, GameId, BaseSession][];
}) {
  const map = useRef(
    new Map<string, BaseSession>(
      (initialSessions ?? []).map(([slug, game, s]) => [keyOf(slug, game, s.matchId), s]),
    ),
  );
  const [version, bump] = useState(0);
  /** Hosted sessions mail their position to absent players (see `mailbox/hosting.ts`). */
  const mailers = useRef(new Map<string, () => void>());
  const stopMail = (k: string) => {
    mailers.current.get(k)?.();
    mailers.current.delete(k);
  };

  const get = useCallback(
    (slug: string, game: GameId, id: string) => map.current.get(keyOf(slug, game, id)),
    [],
  );
  const add = useCallback((slug: string, game: GameId, s: BaseSession): 'added' | 'kept' => {
    const k = keyOf(slug, game, s.matchId);
    const prev = map.current.get(k);
    if (prev === s) return 'added';
    if (prev && isLive(prev)) {
      try {
        s.dispose();
      } catch {
        /* ignore */
      }
      return 'kept';
    }
    if (prev) {
      connLog(`${prev.code} session closed`, { reason: 'replaced by a new session' });
      stopMail(k);
      prev.dispose();
    }
    map.current.set(k, s);
    mailers.current.set(k, startHostMail(slug, game, s as BaseSession & { server?: unknown }));
    bump((n) => n + 1);
    return 'added';
  }, []);
  const liveByCode = useCallback(
    (code: string) => Array.from(map.current.values()).find((s) => s.code === code && isLive(s)),
    [],
  );
  const remove = useCallback(
    (slug: string, game: GameId, id: string, dispose = true, reason = 'removed') => {
      const k = keyOf(slug, game, id);
      const s = map.current.get(k);
      stopMail(k);
      if (s && dispose) {
        connLog(`${s.code} session closed`, { reason });
        s.dispose();
      }
      map.current.delete(k);
      bump((n) => n + 1);
    },
    [],
  );
  const all = useCallback(() => Array.from(map.current.values()), []);

  // Closing the tab: say goodbye so the peer learns immediately instead of via a timeout.
  useEffect(() => {
    const onHide = (e: PageTransitionEvent) => {
      for (const k of [...mailers.current.keys()]) stopMail(k);
      for (const s of map.current.values()) {
        connLog(`${s.code} session closed`, { reason: 'page hidden', persisted: e.persisted });
        try {
          s.dispose();
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  const value = useMemo<SessionRegistry>(
    () => ({ version, get, add, remove, all, liveByCode }),
    [version, get, add, remove, all, liveByCode],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSessionRegistry(): SessionRegistry {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSessionRegistry must be used within SessionRegistryProvider');
  return ctx;
}

/**
 * The live session for a match, typed for the game asking (backgammon's `Session` by default;
 * table games pass their `TableSession` type).
 */
export function useSession<T extends BaseSession = Session>(
  slug: string,
  game: GameId,
  matchId: string | undefined,
): T | undefined {
  const reg = useSessionRegistry();
  return matchId ? (reg.get(slug, game, matchId) as T | undefined) : undefined;
}
